import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { API_ERROR_MESSAGES, IApiErrorData } from "api/axios/types";
import {
  IUser,
  IUserDetails,
  IUserDetailsPayload, // IUserDetails,
  IUserDetailsResponse,
  IUserStatisticsResponse,
  IUserStatsData,
  IUsersResponse,
} from "api/individual/types";
import { fetchData, useFetchData } from "hooks/useFetchData";
import { useMutateData } from "hooks/useMutateData";
import { useLocation } from "react-router-dom";
import { ROUTES } from "routes/constants";
import { ZERO } from "utils/constants";

// import { ZERO } from "utils/constants";
// import { ROUTES } from "routes/constants";
// import { splitStringAtPosition } from "utils";
import { customErrorToast } from "ui/custom-toast";

import { DEFAULT_USER_STATS, IndividualComplianceQueryKeys } from "./consts";
import {
  IIndividualComplianceContextProps,
  IIndividualComplianceProviderProps,
} from "./types";

const INDEX_OFFSET = 1;
const UI_LOADING_TIMEOUT = 100;
export const INDIVIDUAL_LIST_LIMIT = 15;
const IndividualComplianceContext =
  createContext<IIndividualComplianceContextProps>({
    individualsUserStats: null,
    userStats: DEFAULT_USER_STATS,
    setUserStats: () => {
      return null;
    },
    individualsResult: null,
    currentIndividuals: [],
    setCurrentIndividuals: () => {
      return null;
    },
    handleFetchIndividuals: async () => {
      return null;
    },
    isFetchingIndividuals: false,
    isFetchingMoreIndividuals: false,
    individualComplianceUILoading: false,
    setIndividualComplianceUILoading: () => {
      return null;
    },
    rejectReason: "",
    setRejectReason: () => {
      return "";
    },
    handleFetchIndividualDetails: async () => {
      return null;
    },
    isLoadingIndividualDetails: false,
    selectedIndividual: null,
    setSelectedIndividual: () => {
      return null;
    },
    selectedIndividualDetails: null,
    setSelectedIndividualDetails: () => {
      return {};
    },
    // handleGetComplianceTrails: async () => {
    //   return null;
    // },
    // isLoadingComplianceTrails: false,
    // complianceTrails: [],
  });

export const extractKeywordFromPathName = (path: string): string => {
  const segments = path.split("/");
  const lastSegment = segments[segments.length - INDEX_OFFSET];
  const parts = lastSegment.split("-");
  return parts[parts.length - INDEX_OFFSET];
};

export const IndividualComplianceProvider = (
  props: IIndividualComplianceProviderProps
) => {
  const { children } = props;
  const location = useLocation();

  const [isFetchingIndividuals, setIsFetchingIndividuals] = useState(false);
  const [currentIndividuals, setCurrentIndividuals] = useState<IUser[]>([]);
  const [isFetchingMoreIndividuals, setIsFetchingMoreIndividuals] =
    useState(false);
  const [isLoadingIndividualDetails, setIsLoadingIndividualDetails] =
    useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState<IUser | null>(
    null
  );
  const [individualsResult, setIndividualsResult] =
    useState<IUsersResponse | null>(null);
  const [selectedIndividualDetails, setSelectedIndividualDetails] =
    useState<IUserDetails | null>(null);
  const [individualComplianceUILoading, setIndividualComplianceUILoading] =
    useState(false);

  const [userStats, setUserStats] =
    useState<IUserStatsData>(DEFAULT_USER_STATS);

  const [rejectReason, setRejectReason] = useState("");

  const individualsUserStats = useFetchData<IUserStatisticsResponse>(
    [IndividualComplianceQueryKeys.INDIVIDUAL_STATS],
    {
      apiRoute: "/control/personal/compliance/user-stats",
      module: "EARNIPAY_API",
      useEncryption: true,
    },
    {
      enabled() {
        const shouldEnable = window.location.pathname.includes(
          ROUTES.INDIVIDUALS_COMPLIANCE_PAGE
        );
        return shouldEnable;
      },
    }
  );

  const currentComplianceTab = useMemo(() => {
    const status = extractKeywordFromPathName(location.pathname);
    const currentStatus = status === "review" ? "pending" : status;
    return {
      status: currentStatus,
      isReview: status === "review",
    };
  }, [location]);

  const handleFetchIndividuals = async (
    page: number,
    status: string,
    isFetchingMore?: boolean,
    searchQuery?: string
  ): Promise<IUsersResponse | null> => {
    const currentStatus = status === "review" ? "pending" : status;

    if (isFetchingMore) {
      setIsFetchingMoreIndividuals(true);
    } else {
      setIsFetchingIndividuals(true);
    }

    let response = null;
    let apiRoute = `/control/personal/compliance/users?page=${page}&limit=${INDIVIDUAL_LIST_LIMIT}&kycStatus=${currentStatus}`;

    if (searchQuery) {
      apiRoute += `&search=${searchQuery}&searchBy=email,firstName,lastName,firstName_lastName`;
    }
    try {
      response = await fetchData({
        apiRoute,
        module: "EARNIPAY_API",
        useEncryption: true,
      });
    } catch (error) {
      console.error("Error: ", error);
    }
    if (isFetchingMore) {
      setIsFetchingMoreIndividuals(false);
    } else {
      setIsFetchingIndividuals(false);
    }
    return response as unknown as IUsersResponse;
  };

  const handleInitialFetchIndividuals = async () => {
    try {
      const result = await handleFetchIndividuals(
        INDEX_OFFSET,
        currentComplianceTab.status
      );
      setIndividualsResult(result);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  const { mutateAsync: getIndividualUserDetails } = useMutateData<
    IUserDetailsPayload,
    IUserDetailsResponse
  >([IndividualComplianceQueryKeys.INDIVIDUAL_DETAILS], {
    onSuccess: async () => {
      // customSuccessToast({
      //   title: "Individual Compliance",
      //   message: data.message,
      // });
    },
    onError(error) {
      const err = error as unknown as IApiErrorData;
      const message = err.message;
      customErrorToast({ title: "Individual Compliance", message });
    },
  });

  const handleFetchIndividualDetails = async (
    individualId: string
  ): Promise<IUserDetailsResponse | null> => {
    setIsLoadingIndividualDetails(true);
    setSelectedIndividual(null);
    setSelectedIndividualDetails(null);
    let response;
    let result = null;
    try {
      response = await getIndividualUserDetails({
        apiRoute: `/control/personal/compliance/user-details`,
        data: {
          userId: individualId,
        },
        method: "POST",
        module: "EARNIPAY_API",
        useEncryption: true,
      });
      result = response as unknown as IUserDetailsResponse;
      if (!result) {
        customErrorToast({ message: API_ERROR_MESSAGES.NETWORK_ERROR });
      }
      // setSelectedIndividual(result?.data ?? null);
      setSelectedIndividualDetails(result?.data ?? null);
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoadingIndividualDetails(false);

    return result;
  };

  useEffect(() => {
    void handleInitialFetchIndividuals();
  }, []);

  useEffect(() => {
    if (isFetchingIndividuals || isLoadingIndividualDetails) {
      setIndividualComplianceUILoading(true);
    }
    let timer: NodeJS.Timeout;

    if (!isFetchingIndividuals && !isLoadingIndividualDetails) {
      timer = setTimeout(() => {
        setIndividualComplianceUILoading(false);
      }, UI_LOADING_TIMEOUT);
    }
    return () => clearTimeout(timer);
  }, [isFetchingIndividuals, isLoadingIndividualDetails]);

  useEffect(() => {
    // const statsData = individualsUserStats
    //   ? individualsUserStats?.data?.data
    //   : DEFAULT_USER_STATS;

    if (individualsUserStats?.data?.data && userStats.total === ZERO) {
      setUserStats(individualsUserStats?.data?.data);
    }
  }, [individualsUserStats, userStats]);

  return (
    <IndividualComplianceContext.Provider
      value={{
        individualsUserStats,
        userStats,
        setUserStats,
        individualsResult,
        currentIndividuals,
        setCurrentIndividuals,
        handleFetchIndividuals,
        isFetchingIndividuals,
        isFetchingMoreIndividuals,
        handleFetchIndividualDetails,
        isLoadingIndividualDetails,
        selectedIndividual,
        selectedIndividualDetails,
        setSelectedIndividual,
        setSelectedIndividualDetails,
        individualComplianceUILoading,
        setIndividualComplianceUILoading,
        rejectReason,
        setRejectReason,
      }}
    >
      {children}
    </IndividualComplianceContext.Provider>
  );
};

export const useIndividualCompliance = () =>
  useContext(IndividualComplianceContext);
